import { toast } from "react-toastify";
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";
import { NOTIFICATION_IDS } from "store/constants";
import {
  BULK_POLL_MS,
  JOB_STATUS,
} from "pages/Rostering/UpdatedTimeSheet/bulkAnalyse/model/bulkAnalyseModel";
import BULK_ANALYSE_ACTIONS, {
  createBulkAnalyse,
  getBulkAnalyseActive,
  getBulkAnalyseDetail,
  getBulkAnalyseDetailRequest,
  getBulkAnalysePreview,
  dismissBulkAnalyse,
  retryBulkAnalyse,
  setBulkAnalyseJobOpen,
  stopBulkAnalyse,
} from "../actions/bulkAnalyse.actions";
import {
  getBulkAnalyseItemsPagination,
  getBulkAnalyseItemsStatus,
  getBulkAnalyseJob,
  getBulkAnalyseJobOpen,
} from "../selector/bulkAnalyse.selector";
import api from "../services/bulkAnalyse.services";

/** {status, code, message, data} from a parseError'd Laravel error. */
const describeError = (error, fallback) => {
  const body = error?.response?.data || {};
  return {
    status: error?.response?.status || 0,
    code: body.code || "",
    message: body.message || error?.message || fallback,
    data: body.data || null,
  };
};

function* fetchActive() {
  try {
    const response = yield call(api.active);
    yield put(getBulkAnalyseActive.success(response?.data?.job ?? null));
  } catch (error) {
    // Silent: the bar keeps its last state and the next poll or event retries.
    yield put(getBulkAnalyseActive.error(error?.message || ""));
  }
}

function* fetchPreview(action) {
  try {
    yield put(getBulkAnalysePreview.pending);
    const response = yield call(api.preview, action.payload);
    yield put(getBulkAnalysePreview.success(response?.data || null));
  } catch (error) {
    if (error?.isCancelled) return;
    const detail = describeError(error, "Could not load timesheets for these filters.");
    yield put(getBulkAnalysePreview.error(detail.message));
  }
}

function* createRun(action) {
  try {
    yield put(createBulkAnalyse.pending);
    const response = yield call(api.create, action.payload);
    yield put(createBulkAnalyse.success(response?.data?.job || null));
    if (response?.data?.created !== false) {
      toast.success("Bulk analysis started. You can leave this page.");
    }
  } catch (error) {
    const detail = describeError(error, "Could not start bulk analysis.");
    yield put(createBulkAnalyse.error(detail));

    if (detail.status === 409 && detail.code === "BULK_RUN_ACTIVE") {
      // The current analysis is still paused or running. Open that one,
      // not whichever analysis happens to be newest.
      toast.info(detail.message || "A bulk analysis is already in progress. Showing it now.");
      const uuid = detail.data?.job_uuid;
      if (uuid) {
        yield call(fetchDetail, { payload: { uuid, status: "all", page: 1 } });
      } else {
        yield call(fetchActive);
      }
      yield put(setBulkAnalyseJobOpen(true));
      return;
    }
    // 422 (nothing to run / too many) stays inline in the confirm modal.
    if (detail.status !== 422) {
      toast.error(detail.message);
    }
  }
}

function* fetchDetail(action) {
  const { uuid, status = "all", page = 1 } = action.payload || {};
  if (!uuid) return;
  try {
    yield put(getBulkAnalyseDetail.pending);
    const response = yield call(api.detail, { uuid, status, page });
    yield put(getBulkAnalyseDetail.success({ ...(response?.data || {}), status }));
  } catch (error) {
    if (error?.isCancelled) return;
    yield put(getBulkAnalyseDetail.error(error?.message || ""));
    toast.error("Could not load the timesheets in this run.");
  }
}

function* refreshOpenDetail() {
  const open = yield select(getBulkAnalyseJobOpen);
  const job = yield select(getBulkAnalyseJob);
  if (!open || !job?.uuid) return;
  const status = yield select(getBulkAnalyseItemsStatus);
  const pagination = yield select(getBulkAnalyseItemsPagination);
  yield put(
    getBulkAnalyseDetailRequest({
      uuid: job.uuid,
      status,
      page: pagination?.current_page || 1,
    })
  );
}

function* stopRun(action) {
  try {
    yield put(stopBulkAnalyse.pending);
    const response = yield call(api.stop, action.payload.uuid);
    yield put(stopBulkAnalyse.success(response?.data?.job || null));
    toast.info("Bulk analysis paused. Timesheets already analysed are kept.", {
      autoClose: 4000,
    });
    yield call(refreshOpenDetail);
  } catch (error) {
    const detail = describeError(error, "Could not pause the run.");
    yield put(stopBulkAnalyse.error(detail.message));
    toast.error(detail.message, { autoClose: 6000 });
  }
}

function* retryRun(action) {
  try {
    const scope = action.payload.scope || "all";
    yield put(retryBulkAnalyse.pending);
    const response = yield call(api.retry, action.payload.uuid, scope);
    yield put(retryBulkAnalyse.success(response?.data?.job || null));
    toast.success(
      scope === "paused"
        ? "Resuming the timesheets that were paused."
        : "Retrying the timesheets that failed."
    );
    yield call(refreshOpenDetail);
  } catch (error) {
    yield put(retryBulkAnalyse.error(error?.message || ""));
    const detail = describeError(
      error,
      action.payload.scope === "paused"
        ? "Could not resume the run."
        : "Could not retry the failed timesheets."
    );
    toast.error(detail.message);
    if (detail.status === 409) {
      yield call(fetchActive);
    }
  }
}

function* dismissBanner(action) {
  try {
    yield put(dismissBulkAnalyse.pending);
    const response = yield call(api.dismiss, action.payload.uuid);
    yield put(dismissBulkAnalyse.success(response?.data?.job || null));
    toast.success("Bulk analysis closed. You can start a new one.");
  } catch (error) {
    yield put(dismissBulkAnalyse.error(error?.message || ""));
    toast.error(describeError(error, "Could not dismiss the banner.").message);
  }
}

function* onPusher(action) {
  const message = action.data || {};
  const job = yield select(getBulkAnalyseJob);

  // Small debounce: chunks can land close together.
  yield delay(300);

  // A run we do not hold yet, or the final event: load the exact counts
  // (the event carries no queued/running split).
  if (
    !job ||
    job.uuid !== message.job_uuid ||
    message.key === NOTIFICATION_IDS.BULK_ANALYSE_FINISHED
  ) {
    yield call(fetchActive);
  }
  yield call(refreshOpenDetail);
}

/**
 * Safety net for missed Pusher events: while a run is going, reload it every
 * 20s. Ends by itself once the run is final, or on POLL_STOP (page unmount).
 */
function* pollWhileRunning() {
  while (true) {
    const job = yield select(getBulkAnalyseJob);
    if (!job || job.status !== JOB_STATUS.RUNNING) return;
    yield delay(BULK_POLL_MS);
    yield call(fetchActive);
    yield call(refreshOpenDetail);
  }
}

function* pollWatcher() {
  yield race({
    poll: call(pollWhileRunning),
    stop: take(BULK_ANALYSE_ACTIONS.POLL_STOP),
  });
}

export default function* bulkAnalyseSagas() {
  yield all([
    takeLatest(BULK_ANALYSE_ACTIONS.ACTIVE_CALL, fetchActive),
    takeLatest(BULK_ANALYSE_ACTIONS.PREVIEW_CALL, fetchPreview),
    takeLeading(BULK_ANALYSE_ACTIONS.CREATE_CALL, createRun),
    takeLatest(BULK_ANALYSE_ACTIONS.DETAIL_CALL, fetchDetail),
    takeLeading(BULK_ANALYSE_ACTIONS.STOP_CALL, stopRun),
    takeLeading(BULK_ANALYSE_ACTIONS.RETRY_CALL, retryRun),
    takeLeading(BULK_ANALYSE_ACTIONS.DISMISS_CALL, dismissBanner),
    takeLatest(BULK_ANALYSE_ACTIONS.PUSHER_RECEIVED, onPusher),
    takeLatest(BULK_ANALYSE_ACTIONS.POLL_START, pollWatcher),
  ]);
}
